import React from 'react';
import { usePractice } from '../contexts/PracticeContext';
import confetti from 'canvas-confetti';

interface Practice {
  id: number;
  name: string;
  words: string[];
  createdAt: string;
  updatedAt?: string;
}

interface PracticeListProps {
  practices: Practice[];
  onStartPractice: (practice: Practice) => void;
  onAddNew: () => void;
  onEditPractice: (practice: Practice) => void;
}

const PracticeList: React.FC<PracticeListProps> = ({
  practices,
  onStartPractice,
  onAddNew,
  onEditPractice
}) => {
  const { 
    practiceHistory, 
    practiceResults, 
    deletePractice,
    updatePracticeHistory
  } = usePractice();
  
  // Sort practice list by creation date (newest first)
  const sortedPractices = [...practices].sort((a, b) => {
    const dateA = new Date(a.createdAt || 0);
    const dateB = new Date(b.createdAt || 0);
    return dateB.getTime() - dateA.getTime();
  });
  
  const handleStartPractice = (practice: Practice) => {
    updatePracticeHistory(practice.id);
    onStartPractice(practice);
  };
  
  // Function to trigger confetti when starting practice
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  return (
    <div className="px-2 sm:px-3">
      {/* Header is now rendered in App.tsx */}
      
      {/* Welcome message for children */}
      <div className="bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 text-white p-3 sm:p-4 rounded-lg sm:rounded-xl mb-4 sm:mb-6 shadow-lg">
        <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Welcome to Happy Spell! 🎉</h2>
        <p className="text-base sm:text-lg">Choose a list below to start practicing your spelling words!</p>
      </div>
      
      <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {sortedPractices.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center mb-6 border-2 border-dashed border-indigo-300 shadow-md">
            <div className="text-6xl mb-4 animate-bounce inline-block">👋</div>
            <h3 className="text-2xl font-bold text-indigo-800 mb-2">No Spelling Lists Yet!</h3>
            <p className="text-indigo-600 mb-6 text-lg">Let's create your first spelling list!</p>
            <div className="flex justify-center">
              <button
                onClick={onAddNew}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full hover:from-indigo-600 hover:to-purple-700 transition-colors shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-lg flex items-center"
              >
                <span className="text-xl mr-2">✨</span>
                Create My First List
              </button>
            </div>
            {/* <div className="mt-8 bg-yellow-100 p-4 rounded-lg border border-yellow-300 inline-block">
              <h4 className="font-bold text-yellow-800 flex items-center mb-2">
                <span className="text-xl mr-2">💡</span>
                Fun Ideas for Word Lists
              </h4>
              <ul className="text-yellow-800 text-left">
                <li className="flex items-start mb-1">
                  <span className="mr-2">•</span>
                  Animals: cat, dog, elephant
                </li>
                <li className="flex items-start mb-1">
                  <span className="mr-2">•</span>
                  Colors: blue, green, yellow
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Space: planet, star, rocket
                </li>
              </ul>
            </div> */}
          </div>
        ) : (
          sortedPractices.map((practice) => {
            const history = practiceHistory[practice.id] || { lastPracticed: null, timesPracticed: 0 };
            const results = practiceResults[practice.id] || { correct: 0, incorrect: 0 };
            
            // Calculate emoji based on practice history
            let emoji = "📝";
            if (history.timesPracticed > 5) emoji = "🌟";
            else if (history.timesPracticed > 3) emoji = "⭐";
            else if (history.timesPracticed > 0) emoji = "✅";
            
            return (
              <div 
                key={practice.id} 
                className="bg-white rounded-xl shadow-md p-6 border-b-4 border-indigo-500 hover:shadow-xl transition-all transform hover:scale-102"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-start">
                    <span className="text-3xl mr-3">{emoji}</span>
                    <div>
                      <h2 className="text-xl font-bold text-indigo-800 mb-1">{practice.name}</h2>
                      <p className="text-md text-indigo-600 font-medium mb-2">
                        {practice.words.length} {practice.words.length === 1 ? 'word' : 'words'} to practice
                      </p>
                      
                      {history.timesPracticed > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <div className="bg-indigo-100 px-3 py-1 rounded-full inline-block">
                            <span className="text-indigo-800 font-medium">
                              Practiced {history.timesPracticed} {history.timesPracticed === 1 ? 'time' : 'times'}
                            </span>
                          </div>
                          
                          {/* Last practice results */}
                          {(results.correct > 0 || results.incorrect > 0) && (
                            <div className="flex gap-2">
                              <div className="bg-green-100 px-3 py-1 rounded-full inline-flex items-center">
                                <span className="text-green-700 font-medium flex items-center">
                                  <span className="mr-1">✓</span> {results.correct}
                                </span>
                              </div>
                              <div className="bg-red-100 px-3 py-1 rounded-full inline-flex items-center">
                                <span className="text-red-700 font-medium flex items-center">
                                  <span className="mr-1">✗</span> {results.incorrect}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEditPractice(practice)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                      aria-label="Edit practice"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => deletePractice(practice.id)}
                      className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                      aria-label="Delete practice"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => {
                      handleStartPractice(practice);
                      triggerConfetti();
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full hover:from-indigo-600 hover:to-purple-700 transition-colors shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-lg flex items-center"
                  >
                    <span className="text-xl mr-2">🎮</span>
                    Start Practice
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      <div className="flex justify-center mb-8">
        <button
          onClick={onAddNew}
          className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-full hover:from-green-600 hover:to-teal-700 transition-colors shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-lg flex items-center"
        >
          <span className="text-xl mr-2">➕</span>
          Add New List
        </button>
      </div>
    </div>
  );
};

export default PracticeList;
